import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, Trash2 } from "lucide-react";
import { Stars } from "@/pages/PersonalArea";
import { toast } from "sonner";
import Avatar from "@/components/app/Avatar";
import { uploadFile } from "@/lib/upload";

const LABELS = {
  birth_date: "Nascita", birth_place: "Luogo", fiscal_code: "Codice fiscale", address: "Indirizzo", city: "Città",
  iban: "IBAN", personal_email: "Email personale", driving_license: "Patente", emergency_name: "Emergenza",
  emergency_phone: "Tel. emergenza", notes: "Note",
};

export default function EmployeeSheet({ employee, onClose }) {
  const [profile, setProfile] = useState({});
  const [reviews, setReviews] = useState([]);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState("");

  const load = () => {
    api.get(`/employees/${employee.id}/profile`).then((r) => setProfile(r.data.profile || {}));
    api.get(`/employees/${employee.id}/reviews`).then((r) => setReviews(r.data));
  };
  useEffect(() => { if (employee) { load(); } }, [employee?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addReview = async () => {
    if (!score) return toast.error("Scegli un voto da 1 a 5");
    try {
      await api.post(`/employees/${employee.id}/reviews`, { score, feedback });
      toast.success("Valutazione inviata");
      setScore(0); setFeedback(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delReview = async (id) => {
    try { await api.delete(`/reviews/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const [photoPct, setPhotoPct] = useState(null);
  const [photoV, setPhotoV] = useState(employee?.photo_updated_at);
  useEffect(() => { setPhotoV(employee?.photo_updated_at); }, [employee?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const uploadPhoto = async (file) => {
    try {
      setPhotoPct(0);
      const upload_id = await uploadFile(file, setPhotoPct);
      const { data } = await api.post(`/employees/${employee.id}/photo`, { upload_id });
      setPhotoV(data.photo_updated_at); toast.success("Foto aggiornata");
    } catch (e) { toast.error(formatApiError(e)); } finally { setPhotoPct(null); }
  };

  const filled = Object.entries(profile).filter(([, v]) => v);

  return (
    <Dialog open={!!employee} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="employee-sheet-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold flex items-center gap-3">
          {employee && <Avatar user={{ ...employee, photo_updated_at: photoV }} size="w-14 h-14" />}
          {employee?.name}
        </DialogTitle></DialogHeader>
        <label className="text-xs font-semibold text-sky-700 cursor-pointer">
          {photoPct !== null ? `Caricamento ${photoPct}%` : "Cambia foto profilo"}
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} data-testid="employee-sheet-photo-input" />
        </label>
        <section className="space-y-2">
          <h3 className="text-xs uppercase tracking-widest font-bold text-slate-500">Dati anagrafici</h3>
          {filled.length === 0 ? <p className="text-sm text-slate-400">Non ancora compilati dal dipendente.</p> : (
            <dl className="grid grid-cols-3 gap-x-3 gap-y-1 text-sm" data-testid="employee-sheet-profile">
              {filled.map(([k, v]) => (<div key={k} className="contents"><dt className="text-slate-500">{LABELS[k] || k}</dt><dd className="col-span-2 text-slate-900 break-words">{v}</dd></div>))}
            </dl>
          )}
        </section>
        <section className="space-y-3 pt-4 border-t border-slate-100">
          <h3 className="text-xs uppercase tracking-widest font-bold text-slate-500">Nuova valutazione</h3>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <button key={i} type="button" onClick={() => setScore(i)} data-testid={`review-star-${i}`}>
                <Star className={`w-7 h-7 transition-colors ${i <= score ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
              </button>
            ))}
          </div>
          <Textarea rows={3} placeholder="Feedback per il dipendente" value={feedback} onChange={(e) => setFeedback(e.target.value)} data-testid="review-feedback-input" />
          <Button onClick={addReview} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="review-submit-button">Invia valutazione</Button>
        </section>
        <ul className="space-y-2 pt-4 border-t border-slate-100" data-testid="employee-sheet-reviews">
          {reviews.map((r) => (
            <li key={r.id} className="border border-slate-200 rounded-lg p-3 text-sm">
              <div className="flex items-center justify-between">
                <Stars score={r.score} />
                <button onClick={() => delReview(r.id)} className="text-slate-400 hover:text-rose-600" data-testid={`delete-review-${r.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              {r.feedback && <p className="text-slate-700 mt-1 whitespace-pre-wrap">{r.feedback}</p>}
              <div className="text-xs text-slate-400 mt-1">{r.created_at.slice(0, 10)}</div>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
